import React, { useEffect, useState } from "react";
import AppLayout from "../../core/layouts/AppLayout";
import "../../styles/listing.scss";

import Moment from "react-moment";
import moment from "moment";
import classnames from "classnames";
import {
  ListTable,
  ListTableColumn,
  ListHeader,
  Dict,
  FilterValueType,
  getFilters,
  PaginatedApiResponse,
  ListSort,
  SortOrder,
  Showroom,
  QueryFilter,
  ListFilters,
  ListHeaderSearch,
  MultiSelectFilter,
  ListColumnSettings,
  SavedFilter,
  ListEntityType,
  Column,
  City,
} from "../../core";
import { useMasterData } from "../../core/hooks/useMasterData";
import api from "../../api";
import { useDataLoader, usePerformAction } from "react-remote-data-hooks";
import { ContainerLoader, Spinner } from "../../components/loader";
import { useAppSettings } from "../../core/hooks/useAppSettings";
import { useHistory, useLocation } from "react-router-dom";
import queryString, { ParsedQuery } from "query-string";

import DateRangeListFilter, {
  IDateRange,
} from "../../core/components/list-filters/DateRangeListFilter";
import Pagination from "../../core/components/pagination/Pagination";
import { RouteKeys } from "../routes/route-keys";
import { Property, PropertyFeatured, PropertyPurpose, propertyCurrentStatus, FurnishingStatus, SoldOutStatus } from "../../core/api/models/property";
import { toastr } from "react-redux-toastr";
import { decodeHtmlEntities } from '../../utils/common'
import MinMaxFilter from "../../core/components/list-filters/MinMaxFilter";

const PropertyListing: React.FunctionComponent = () => {
  const history = useHistory();
  const params = useLocation().search;
  const [search, setSearch] = useState("");

  const [filters, setFilters] = useState<Dict<FilterValueType>>({});
  const [cities, setCities] = useState<any>([]);
  const [projectType, setProjectType] = useState<any>([]);
  const [propertyType, setPropertyType] = useState<any>([]);
  const [floorPreferences, setFloorPreferences] = useState<any>([]);
  const [amenities, setAmenities] = useState<any>([]);

  const { data: leadSettings, reload: reloadSettings } = useDataLoader<{
    filters: SavedFilter[];
    columnSettings?: ListColumnSettings;
  }>(() => api.settings.getListColumnSettings(ListEntityType.Property));

  const {
    performAction: saveFilter,
    isPerforming: savingFilter,
  } = usePerformAction(
    (fls: Dict<FilterValueType>, name: string) =>
      api.settings.saveFilter(fls, name, ListEntityType.Property),
    () => {
      toastr.success("Success", "Filter Saved");
      reloadSettings();
    },
    () => {
      toastr.error("Error", "Unable to save filter");
    }
  );

  const { performAction: removeFilter } = usePerformAction(
    (_id: string) => api.settings.removeSavedFilter(_id),
    () => {
      toastr.success("Success", "Filter Removed");
      reloadSettings();
    },
    () => {
      toastr.error("Error", "Unable to remove filter");
    }
  );

  const { performAction: saveColumnSettings } = usePerformAction(
    (cols: Column[]) =>
      api.settings.saveListColumnSettings(cols, ListEntityType.Property),
    reloadSettings
  );

  const loadFromQuery = () => {
    const queryFilter: QueryFilter = {};
    const queries: ParsedQuery<string> = queryString.parse(params);
    queryFilter.query = queries.query as string;
    if (queries.filters) {
      queryFilter.filters = JSON.parse(queries.filters?.toString());
    }
    if (queries.page) {
      queryFilter.page = Number(queries.page || 1);
    }
    if (queries.perPage) {
      queryFilter.perPage = Number(queries.perPage || 1);
    }
    if (queries.startDate) {
      queryFilter.startDate = moment(queries.startDate as string).toDate();
    }
    if (queries.endDate) {
      queryFilter.endDate = moment(queries.endDate as string).toDate();
    }

    if (queries.sort) {
      queryFilter.sort = JSON.parse(queries.sort?.toString());
    }

    if (queries.listView) {
      queryFilter.listView = queries.listView?.toString();
    }

    return queryFilter;
  };

  const [listView, setListView] = useState(
    loadFromQuery().listView || "property"
  );

  const [dateFilters, setDateFilters] = useState<IDateRange | undefined>({
    startDate: loadFromQuery()?.startDate,
    endDate: loadFromQuery()?.endDate,
  });

  const [state, setState] = React.useState([
    {
      startDate: new Date(),
      endDate: null,
      key: "selection",
    },
  ]);

  const [sort, setSort] = useState<ListSort>({
    sortBy: loadFromQuery()?.sort?.sortBy || "createdAt",
    sortOrder: loadFromQuery()?.sort?.sortOrder || SortOrder.DESC,
  });

  const [page, setPage] = React.useState(loadFromQuery()?.page || 1);
  const [perPage, setPerPage] = React.useState(loadFromQuery()?.perPage || 20);

  const { data, loading, loaded, reload } = useDataLoader<
    PaginatedApiResponse<Property> & { lastUpdatedAt: Date }
  >(
    (
      searchTerm: string | undefined,
      searchFilters: Dict<FilterValueType>,
      currentPage: number,
      pageCount: number,
      searchOrder?: ListSort,
      dates?: IDateRange
    ) => {
      let currentFilter = {
        ...getFilters(loadFromQuery()?.filters || searchFilters),
      };

      if (currentFilter.hasOwnProperty('MinMaxArea')) {
        const minArea = currentFilter?.MinMaxArea?.startValue;
        const maxArea = currentFilter?.MinMaxArea?.endValue;
        delete currentFilter.MinMaxArea; // Remove the MinMaxArea property from the object
        currentFilter = { ...currentFilter, minArea, maxArea }
      }
      if (currentFilter.hasOwnProperty('MinMaxPrice')) {
        const minPrice = currentFilter?.MinMaxPrice?.startValue;
        const maxPrice = currentFilter?.MinMaxPrice?.endValue;
        delete currentFilter.MinMaxPrice; // Remove the MinMaxArea property from the object
        currentFilter = { ...currentFilter, minPrice, maxPrice }
      }

      if (currentFilter.salesStatus?.includes("ConfirmedSale")) {
        currentFilter.salesStatus = [
          ...currentFilter.salesStatus?.filter(
            (x: any) => x !== "ConfirmedSale"
          ),
          "Ordered",
          "OrderConfirmed",
        ];
      }

      return api.property.getPropertyList({
        ...currentFilter,
        page: currentPage,
        query: loadFromQuery()?.query || searchTerm,
        sortBy: searchOrder?.sortBy,
        sortOrder: searchOrder?.sortOrder,
        perPage: pageCount,
      });
    },
    [search, filters, page, perPage, sort, dateFilters]
  );

  useEffect(() => {
    if (loaded) {
      setPage(1);
      onChangeFilter(filters, search, 1, perPage, dateFilters, sort);
      reload(search, filters, 1, perPage, sort, dateFilters);
      // reload(search, filters, dateFilters, sort);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filters, sort, perPage, dateFilters]);

  useEffect(() => {
    if (loaded) {
      reload(search, filters, page, perPage, sort, dateFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  React.useEffect(() => {
    fetchAllFilterOptions()
    const filterResonse = loadFromQuery();

    if (filterResonse.query) {
      setSearch(filterResonse.query as string);
    }

    if (filterResonse.filters) {
      if (
        filterResonse.filters?.autolineStatus &&
        Array.isArray(filterResonse.filters.autolineStatus)
      ) {
        filterResonse.filters.autolineStatus = filterResonse.filters.autolineStatus?.filter(
          (x: any) => x.value !== "PreVin" && x.value !== "Produced"
        );
        if (!filterResonse.filters.autolineStatus?.length) {
          delete filterResonse.filters.autolineStatus;
        }
      }
      setFilters(filterResonse.filters);
    }

    if (filterResonse.page) {
      setPage(filterResonse.page);
    }
    if (filterResonse.perPage) {
      setPerPage(filterResonse.perPage);
    }
  }, []);

  const [selected, setSelected] = useState<string[]>([]);

  const onSaveFilter = (name: string) => {
    saveFilter(filters, name);
  };

  const onRemoveFilter = (_id: string) => {
    removeFilter(_id);
  };

  const onChangeFilter = (
    searchFilters: Dict<FilterValueType>,
    searchValue: string,
    currentPage: number,
    numberOfPages: number,
    dates?: IDateRange,
    sortOption?: ListSort
  ) => {
    let query = `query=${encodeURIComponent(
      searchValue
    )}&page=${currentPage}&perPage=${numberOfPages}`;

    if (
      getFilters(searchFilters) &&
      Object.keys(getFilters(searchFilters)).length
    ) {
      query =
        query + `&filters=` + encodeURIComponent(JSON.stringify(searchFilters));
    }
    if (dates?.startDate) {
      query =
        query + `&startDate=` + moment(dates?.startDate).format("DD-MMM-YYYY");
    }
    if (dates?.endDate) {
      query =
        query + `&endDate=` + moment(dates?.endDate).format("DD-MMM-YYYY");
    }
    if (sortOption && sortOption.sortBy) {
      query = query + `&sort=` + JSON.stringify(sortOption);
    }
    history.replace(`/properties?${query}`);
  };

  const fetchAllFilterOptions = async () => {
    try {
      const cityData = await api.AddNewRequirement.loadAllCities();
      const cityOptions = cityData?.map((item: City) => ({
        value: item.cityName,
        name: decodeHtmlEntities(item?.cityName),
      }));
      cityOptions.sort((a, b) => a.name.localeCompare(b.name));
      // Set city data to the form field
      setCities(cityOptions);
    } catch (error) {
      // console.log("Failed to fetch city data:", error);
    }

    try {
      const floorData = await api.AddNewRequirement.getPreferredFloor();
      const floorDataOptions = (floorData || [])
        .map((item: any) => ({
          value: item,
          name: item,
        }))
        .sort((a: { name: number }, b: { name: number }) => a.name - b.name);
      setFloorPreferences(floorDataOptions || []);
    } catch (error) {
      // console.log("Failed to fetch preferred floor:", error);
    }

    try {
      const amenityData = await api.AddNewRequirement.getAmenity({
        search: "",
      });
      const amenityDataOptions = amenityData?.map((item: any) => ({
        value: item.amenity,
        name: decodeHtmlEntities(item?.amenity),
      }));
      setAmenities(amenityDataOptions);
    } catch (error) {
      // console.log("Failed to fetch amenities data:", error);
    }

    try {
      const propertyTypeData = await api.AddNewRequirement.getPropertyType();
      const subTypes = propertyTypeData.reduce((acc: any, item: any) => {
        acc.push(
          ...item.subTypes.map(
            (subType: { propertyTypeKey: any; propertyTypeName: any }) => ({
              value: subType.propertyTypeKey,
              name: decodeHtmlEntities(subType.propertyTypeName),
            })
          )
        );
        return acc;
      }, []);
      setPropertyType(subTypes);
      // setValue('preferredLocations', []);
    } catch (error) {
      // console.log("Failed to fetch property type data:", error);
    }

    try {
      const projectTypeData = await api.AddNewRequirement.getProjectType();
      const subTypes = projectTypeData.reduce((acc: any, item: any) => {
        acc.push(
          ...item.subTypes.map(
            (subType: { propertyTypeKey: any; propertyTypeName: any }) => ({
              value: subType.propertyTypeKey,
              name: decodeHtmlEntities(subType.propertyTypeName),
            })
          )
        );
        return acc;
      }, []);
      // console.log("subTypes", subTypes);
      setProjectType(subTypes);
    } catch (error) {
      // console.log("Failed to fetch project type data:", error);
    }


  }

  const columns = [
    <ListTableColumn
      label="Property Name "
      source="propertyName"
      column="propertyName"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Featured "
      source="propertyFeatured"
      column="propertyFeatured"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Key"
      source="propertyKey"
      column="propertyKey"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Purpose"
      source="propertyPurpose"
      column="propertyPurpose"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="City Location Name"
      source="cityLocName"
      column="cityLocName"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Current Status"
      source="propertyCurrentStatus"
      column="propertyCurrentStatus"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Added Date"
      source="propertyAddedDate"
      column="propertyAddedDate"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Update Date"
      source="propertyUpdateDate"
      column="propertyUpdateDate"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Description"
      source="propertyDescription"
      column="propertyDescription"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Furnishing Status"
      source="furnishingStatus"
      column="furnishingStatus"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Type Name"
      source="propertyTypeName"
      column="propertyTypeName"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Is Negotiable"
      source="isNegotiable"
      column="isNegotiable"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Price On Request"
      source="priceOnReq"
      column="priceOnReq"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Possession Date"
      source="possessionDate"
      column="possessionDate"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Is Offer"
      source="isOffer"
      column="isOffer"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Offer Discount"
      source="propertyOfferDiscount"
      column="propertyOfferDiscount"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Sold Out Status"
      source="propertySoldOutStatus"
      column="propertySoldOutStatus"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Sold Out Price"
      source="propertySoldOutPrice"
      column="propertySoldOutPrice"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Open House Status"
      source="openhouseStatus"
      column="openhouseStatus"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Price"
      source="propertyPrice"
      column="propertyPrice"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Price Map"
      source="propertyPriceMap"
      column="propertyPriceMap"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Favourite"
      source="propertyFavourite"
      column="propertyFavourite"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Bedroom"
      source="propertyBedRoom"
      column="propertyBedRoom"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Square Feet"
      source="propertySqrFeet"
      column="propertySqrFeet"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Plot Area"
      source="propertyPlotArea"
      column="propertyPlotArea"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Plan Property Details"
      source="planPropertyDetails"
      column="planPropertyDetails"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Plan Enquiry"
      source="planEnquiry"
      column="planEnquiry"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Plan Enquiry Count"
      source="planEnquiryCount"
      column="planEnquiryCount"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Plan Enquiry Count Balance"
      source="planEnquiryCountBalance"
      column="planEnquiryCountBalance"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Type"
      source="type"
      column="type"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Property Price Type"
      source="properyPriceType"
      column="properyPriceType"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Number of Seats"
      source="numberOfSeats"
      column="numberOfSeats"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
    <ListTableColumn
      label="Public Display"
      source="publicDisplay"
      column="publicDisplay"
      showDefault
      sortable
      settingsGroup="Property"
    ></ListTableColumn>,
  ];

  const filterRow = (
    <ListFilters
      key="filter-row"
      savedFilters={leadSettings?.filters || []}
      onSaveFilter={onSaveFilter}
      isSavingFilter={savingFilter}
      onRemoveFilter={onRemoveFilter}
      filters={filters}
      onFilter={(searchFilters: Dict<FilterValueType>) => {
        onChangeFilter(searchFilters, search, page, perPage, dateFilters, sort);
        setFilters(searchFilters);
      }}
    >
      <MultiSelectFilter
        label="Property Type Name"
        filterKey="propertyTypes"
        key="propertyTypes"
        options={propertyType}
        valueField="value"
        labelField="name"
      />
      <MultiSelectFilter
        label="City Name"
        filterKey="cityNames"
        key="cityNames"
        options={cities}
        valueField="value"
        labelField="name"
      />
      <MultiSelectFilter
        label="Floor Name"
        filterKey="floors"
        key="floors"
        options={floorPreferences}
        valueField="value"
        labelField="name"
      />
      <MultiSelectFilter
        label="Aminities"
        filterKey="amenities"
        key="amenities"
        options={amenities}
        valueField="value"
        labelField="name"
      />
      <MultiSelectFilter
        label="Property Featured"
        filterKey="propertyFeatured"
        key="propertyFeatured"
        options={Object.entries(PropertyFeatured).map(([name, val]) => ({
          label: name,
          value: val,
        }))}
        valueField="value"
        labelField="label"
      />
      <MultiSelectFilter
        label="Property Purpose"
        filterKey="propertyPurpose"
        key="propertyPurpose"
        options={Object.entries(PropertyPurpose).map(([name, val]) => ({
          label: name,
          value: val,
        }))}
        valueField="value"
        labelField="label"
      />
      <MultiSelectFilter
        label="Property Current Status"
        filterKey="propertyCurrentStatus"
        key="propertyCurrentStatus"
        options={Object.entries(propertyCurrentStatus).map(([name, val]) => ({
          label: val,
          value: val,
        }))}
        valueField="value"
        labelField="label"
      />
      <MultiSelectFilter
        label="Furnishing Status"
        filterKey="furnishingStatus"
        key="furnishingStatus"
        options={Object.entries(FurnishingStatus).map(([name, val]) => ({
          label: val,
          value: val,
        }))}
        valueField="value"
        labelField="label"
      />
      <MultiSelectFilter
        label="Sold Out Status"
        filterKey="soldOutStatus"
        key="soldOutStatus"
        options={Object.entries(SoldOutStatus).map(([name, val]) => ({
          label: name,
          value: val,
        }))}
        valueField="value"
        labelField="label"
      />
      <MultiSelectFilter
        label="Project Type Name"
        filterKey="projectTypes"
        key="projectTypes"
        options={projectType}
        valueField="value"
        labelField="name"
      />
      <MinMaxFilter
        label="Min & Max Price"
        filterKey="MinMaxPrice"
        feildOnePlaceholder="Min Price"
        feildTwoPlaceholder="Max Price"
        feildOneLabel="Min Price"
        feildTwoLabel="Max Price"
      />
      <MinMaxFilter
        label="Min & Max Area"
        filterKey="MinMaxArea"
        feildOnePlaceholder="Min Area"
        feildTwoPlaceholder="Max Area"
        feildOneLabel="Min Area"
        feildTwoLabel="Max Area"
      />

    </ListFilters>
  );

  const headerActions = [
    <ListHeaderSearch
      placeholder="Property Name"
      serachTerm={search}
      onSearch={(val: string) => {
        onChangeFilter(filters, val, page, perPage, dateFilters, sort);
        setSearch(val);
      }}
    />,
  ];
  return (
    <AppLayout>
      <>
        <ListHeader additionalRows={[filterRow]} actions={headerActions}>
          <div className="d-flex align-items-center">
            <h2 className="m-0 d-flex flex-column">
              Properties
              {data && (
                <span className="font-xs text-muted font-weight-semibold d-block pt-2">
                  {data.count} Total
                  <span className="font-weight-normal ml-1">
                    Updated <Moment fromNow date={data.lastUpdatedAt} />
                  </span>
                  {selected.length > 0 && (
                    <span className="font-weight-normal ml-1">
                      Selected {selected.length} items
                    </span>
                  )}
                </span>
              )}
            </h2>
            <div className="view-switch ml-lg-5 ml-md-4 ml-auto pl-3 d-flex">
              <div className="border rounded-sm d-flex font-normal"></div>
            </div>
          </div>
        </ListHeader>
        <>
          {(!loaded || loading) && (
            <ContainerLoader
              height={window.innerHeight - 300}
            ></ContainerLoader>
          )}
          {loaded && !loading && (
            <ListTable
              data={data?.data}
              showColumnSettings
              selectedValues={selected}
              onSelect={setSelected}
              onSort={(sortKey: string, sortOrder?: SortOrder) => {
                setSort({ sortBy: sortKey, sortOrder });
                onChangeFilter(filters, search, page, perPage, dateFilters, {
                  sortBy: sortKey,
                  sortOrder,
                });
              }}
              sort={sort}
              trProps={(d: Property) => {
                return {
                  className: classnames({
                    new: false,
                    "cursor-pointer": true,
                  }),
                  onClick: () =>
                    history.push(
                      RouteKeys.PropertyDetails.replace(
                        ":propertyId",
                        d._id || ""
                      )
                    ),
                };
              }}
            >
              {columns}
            </ListTable>
          )}
          <Pagination
            currentPage={page}
            totalPages={data?.totalPages}
            onPageChange={(pg: number) => {
              setPage(pg);
              onChangeFilter(filters, search, pg, perPage, dateFilters, sort);
            }}
            perPage={perPage}
            onPerPageChange={(pg: number) => {
              setPerPage(pg);
              onChangeFilter(filters, search, page, pg, dateFilters, sort);
            }}
            count={data?.count}
            label="properties"
          />
        </>
      </>
    </AppLayout>
  );
};

export default PropertyListing;
